/**
 * useDocWorkflow — Submit / Cancel / Amend mutations for submittable documents.
 *
 * Encapsulates the Frappe document lifecycle (docstatus 0→1→2→0) with
 * permission-derived flags and TanStack Query mutations.
 *
 * Usage: const { submitDoc, canSubmit, isSubmitting, ... } = useDocWorkflow({ ... });
 *
 * Notes:
 * - frappe.client.submit takes { doc: fullDocument }
 * - frappe.client.cancel takes { doctype, name }
 * - frappe.client.amend does NOT exist — uses custom zaviago_backend endpoint
 * - This hook does NOT manage UI (dialogs, toasts) — that belongs in Epic 02/03
 */

import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import type { DocTypePermissions } from "@/lib/erp/meta";
import { captureDocSubmitted, captureDocCancelled, captureDocAmended } from "@/lib/analytics/posthog-events";

export interface UseDocWorkflowOptions {
  doctype: string;
  name: string;
  formData: Record<string, any>;
  isSubmittable?: boolean;
  permissions?: DocTypePermissions;
}

export interface UseDocWorkflowReturn {
  submitDoc: () => Promise<any>;
  cancelDoc: () => Promise<any>;
  amendDoc: () => Promise<any>;
  isSubmitting: boolean;
  isCancelling: boolean;
  isAmending: boolean;
  /** Whether the Submit button should be shown (draft + permission) */
  canSubmit: boolean;
  /** Whether the Cancel action should be shown (submitted + permission) */
  canCancel: boolean;
  /** Whether the Amend button should be shown (cancelled + permission) */
  canAmend: boolean;
}

/** Strip HTML tags from Frappe error messages for clean toast display */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

/** Extract a user-friendly message from a Frappe error */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Frappe sometimes wraps errors in JSON with an exc field
    try {
      const parsed = JSON.parse(error.message);
      if (parsed._server_messages) {
        const msgs = JSON.parse(parsed._server_messages);
        const first = typeof msgs === "string" ? msgs : msgs[0];
        const inner = typeof first === "string" ? JSON.parse(first) : first;
        return stripHtml(inner.message || inner.exc || error.message);
      }
    } catch {
      // Not JSON — use raw message
    }
    return stripHtml(error.message);
  }
  return String(error);
}

export function useDocWorkflow({
  doctype,
  name,
  formData,
  isSubmittable = false,
  permissions,
}: UseDocWorkflowOptions): UseDocWorkflowReturn {
  const queryClient = useQueryClient();

  const invalidateDocQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["get", doctype, name] });
    queryClient.invalidateQueries({ queryKey: ["list", doctype] });
    queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
    queryClient.invalidateQueries({ queryKey: ["doc-sidebar", doctype, name] });
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      const client = getERPClient();
      return client.call("frappe.client.submit", { doc: formData });
    },
    onSuccess: () => {
      invalidateDocQueries();
      captureDocSubmitted(doctype, name);
    },
    onError: (error) => {
      // Error message extraction for consumer use via mutation.error
      console.error(`Submit failed for ${doctype}/${name}:`, extractErrorMessage(error));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const client = getERPClient();
      return client.call("frappe.client.cancel", { doctype, name });
    },
    onSuccess: () => {
      invalidateDocQueries();
      captureDocCancelled(doctype, name);
    },
    onError: (error) => {
      console.error(`Cancel failed for ${doctype}/${name}:`, extractErrorMessage(error));
    },
  });

  const amendMutation = useMutation({
    mutationFn: async () => {
      const client = getERPClient();
      // frappe.client.amend does NOT exist — custom endpoint
      return client.call("zaviago_backend.api.meta_bundle.amend_doc", {
        doctype,
        name,
      });
    },
    onSuccess: () => {
      // Amend creates a new doc — invalidate list queries
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
      captureDocAmended(doctype, name);
    },
    onError: (error) => {
      console.error(`Amend failed for ${doctype}/${name}:`, extractErrorMessage(error));
    },
  });

  // Permission-derived flags
  const docstatus = formData?.docstatus;
  const canSubmit = useMemo(
    () => !!isSubmittable && docstatus === 0 && permissions?.submit !== false,
    [isSubmittable, docstatus, permissions?.submit],
  );
  const canCancel = useMemo(
    () => !!isSubmittable && docstatus === 1 && permissions?.cancel !== false,
    [isSubmittable, docstatus, permissions?.cancel],
  );
  const canAmend = useMemo(
    () => !!isSubmittable && docstatus === 2 && permissions?.amend !== false,
    [isSubmittable, docstatus, permissions?.amend],
  );

  return {
    submitDoc: submitMutation.mutateAsync,
    cancelDoc: cancelMutation.mutateAsync,
    amendDoc: amendMutation.mutateAsync,
    isSubmitting: submitMutation.isPending,
    isCancelling: cancelMutation.isPending,
    isAmending: amendMutation.isPending,
    canSubmit,
    canCancel,
    canAmend,
  };
}

export { extractErrorMessage };
